// GroundingAlarm — the loud, OVERRIDABLE grounding-coverage alarm, pure.
// Composes Icon, Text, Badge, Button.
//
// Extracted from `ExportFlow`'s `review` step (originally the ONLY seam
// citation-load-bearing-spec.md wired — see that spec's §2, "Package for
// MVP... add a lighter confirm-time nudge later"). This is that nudge,
// generalised into a component neither seam owns exclusively: the export
// flow renders it before packaging, and a confirm surface can render the
// identical alarm before blessing a draft. One component, one visual
// vocabulary, one accessibility contract — a citation flagged here reads
// identically wherever the author meets it.
//
// Honest-signal rule (spec §3, unchanged): the alarm shows EVIDENCE, never
// a percentage. `unsupportedTerms` (the soft "these terms don't appear in
// your sources" floor signal) is the only content; the overlap coverage
// NUMBER never appears.
//
// Alarm-discipline: the override (`onPrimary`) is ALWAYS enabled, in every
// register — an alarm informs, it never seizes the decision. Per the
// ratified warn-and-allow decision (decisions.jsonl `1f6b1386`), MVP
// callers always pass `advisory: true`; `advisory: false` exists only as
// forward-compat for the deferred opt-in hard gate the citation-load-
// bearing spec's Open Decision B describes (a future export-only option) —
// even then this component's contract is unchanged, only the description
// line's register switches.

export type { GroundingAlarmProps };
export { GroundingAlarm };

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import "./GroundingAlarm.css";

type GroundingAlarmProps = {
    readonly unsupportedTerms: readonly string[];
    // Advisory (warn-and-allow) vs a hard gate — switches ONLY the
    // description line's register (citation-load-bearing-spec.md §4's
    // exportAdvisory/exportGate split). The override stays primary and
    // enabled either way.
    readonly advisory: boolean;
    // The override action — ALWAYS enabled (alarm-discipline).
    readonly primaryLabel: ReactNode;
    readonly onPrimary: () => void;
    // The "go look first" exit — optional.
    readonly secondaryLabel?: ReactNode;
    readonly onSecondary?: () => void;
    readonly className?: string;
};

const GroundingAlarm = ({
    unsupportedTerms,
    advisory,
    primaryLabel,
    onPrimary,
    secondaryLabel,
    onSecondary,
    className,
}: GroundingAlarmProps): ReactNode => {
    const hasUnsupported = unsupportedTerms.length > 0;
    const root = `bilda-grounding-alarm${className ? ` ${className}` : ""}`;

    return (
        <section
            className={root}
            role="alertdialog"
            aria-labelledby="bilda-grounding-alarm-title"
            aria-describedby="bilda-grounding-alarm-desc"
        >
            <header className="bilda-grounding-alarm__head">
                <span
                    className="bilda-grounding-alarm__mark"
                    aria-hidden="true"
                >
                    <Icon name="warning" size="md" />
                </span>
                <div>
                    <Text as="p" size="lg" weight="semibold">
                        <span id="bilda-grounding-alarm-title">
                            <Trans>
                                Some statements may lack support in the
                                knowledge bank
                            </Trans>
                        </span>
                    </Text>
                    <Text as="p" size="sm" tone="dim">
                        <span id="bilda-grounding-alarm-desc">
                            {advisory ? (
                                // Kept byte-identical to the string ExportFlow
                                // shipped (an existing sv.po translation keys
                                // off it) — the only LIVE caller today is
                                // still the package seam. A confirm caller
                                // wanting different wording is a follow-up
                                // (add a `description` override prop + its
                                // own translation) once one actually exists.
                                <Trans>
                                    You can still package this material. Review
                                    the flagged items below, or ship as-is — the
                                    choice is yours.
                                </Trans>
                            ) : (
                                <Trans>
                                    The export gate is on — resolve these before
                                    this material can be exported.
                                </Trans>
                            )}
                        </span>
                    </Text>
                </div>
            </header>

            {hasUnsupported ? (
                <div className="bilda-grounding-alarm__unsupported">
                    <Text size="sm" weight="medium">
                        <Trans>These terms don't appear in your sources:</Trans>
                    </Text>
                    <div className="bilda-grounding-alarm__terms">
                        {unsupportedTerms.map((term) => (
                            <Badge key={term} tone="neutral">
                                {term}
                            </Badge>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="bilda-grounding-alarm__actions">
                {/* Override is the PRIMARY, always-enabled action — the alarm
                    informs, it does not block (alarm-discipline). */}
                <Button variant="primary" onClick={onPrimary}>
                    {primaryLabel}
                </Button>
                {onSecondary ? (
                    <Button variant="secondary" onClick={onSecondary}>
                        {secondaryLabel}
                    </Button>
                ) : null}
            </div>
        </section>
    );
};
